import React, { useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { useSnackbar } from "notistack";

import StickyNote2Outlined from "@mui/icons-material/StickyNote2Outlined";
import CloseIcon from "@mui/icons-material/Close";

import api from "/imports/api/client";
import { NO_CAPTURE } from "/imports/libs/analytics";
import { labelFor } from "../attendanceHelpers";
import NoteCallout from "./NoteCallout";
import AttendanceRow from "./AttendanceRow";

function AttendanceDialog({ booking, onClose, onChanged, onNote }) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const { enqueueSnackbar } = useSnackbar();
  const open = Boolean(booking);
  const [statuses, setStatuses] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!booking) return;
    const seed = {};
    (booking.clients || []).forEach((c) => {
      seed[c.bookishClientId] = c.status || "NEW";
    });
    setStatuses(seed);
    setDirty(false);
  }, [booking]);

  const handleSet = async (client, next) => {
    if (!next || next === statuses[client.bookishClientId]) return;
    const prev = statuses[client.bookishClientId];
    setStatuses((s) => ({ ...s, [client.bookishClientId]: next }));
    setSavingId(client.bookishClientId);
    try {
      await api.markTrainerAttendance({ bookishClientId: client.bookishClientId, status: next });
      setDirty(true);
      const msg = next === "NEW" ? `${client.name || "Client"} reset to New booking` : `${client.name || "Client"} marked ${labelFor(next)}`;
      enqueueSnackbar(msg, { variant: "success", anchorOrigin: { vertical: "top", horizontal: "center" } });
    } catch (err) {
      setStatuses((s) => ({ ...s, [client.bookishClientId]: prev }));
      enqueueSnackbar(err.message || "Couldn't update attendance", { variant: "error", anchorOrigin: { vertical: "top", horizontal: "center" } });
    } finally {
      setSavingId(null);
    }
  };

  const handleClose = () => {
    if (dirty) onChanged?.(statuses);
    onClose();
  };

  // Hand off to the note dialog, flushing any attendance edits first so they
  // aren't lost when this one unmounts.
  const handleNote = () => {
    if (dirty) onChanged?.(statuses);
    onNote?.(booking);
  };

  const clients = booking?.clients || [];
  const trainerNote = booking?.trainerNote?.text || "";
  const hasNote = Boolean(trainerNote);
  const bookingNote = booking?.bookingNote || "";

  return (
    <Dialog className={NO_CAPTURE} open={open} onClose={handleClose} fullWidth maxWidth="sm" fullScreen={fullScreen}>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, fontWeight: 700 }}>
        <Box component="span" sx={{ flex: 1, minWidth: 0 }}>
          Notes, Attendance &amp; Levels
        </Box>
        <IconButton edge="end" size="small" onClick={handleClose} aria-label="Close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ px: { xs: 2, sm: 3 } }}>
        {bookingNote && <NoteCallout variant="booking" text={bookingNote} sx={{ mb: trainerNote ? 1.25 : 2 }} />}
        {trainerNote && <NoteCallout variant="trainer" text={trainerNote} sx={{ mb: 2 }} />}
        {clients.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            No clients on this booking.
          </Typography>
        ) : (
          clients.map((c, i) => (
            <Box key={c.bookishClientId}>
              {i > 0 && <Divider sx={{ my: 2 }} />}
              <AttendanceRow client={c} value={statuses[c.bookishClientId]} saving={savingId === c.bookishClientId} onSet={handleSet} />
            </Box>
          ))
        )}
      </DialogContent>
      <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 1.5, justifyContent: "space-between" }}>
        {onNote ? (
          <Button size="small" variant="outlined" color="primary" startIcon={<StickyNote2Outlined />} onClick={handleNote} sx={{ fontWeight: 600 }}>
            {hasNote ? "Edit note" : "Add note"}
          </Button>
        ) : (
          <span />
        )}
        <Button variant="contained" onClick={handleClose} sx={{ minWidth: "7.5rem" }}>
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AttendanceDialog;
